// Upload helpers shared by every file-picking UI (profile hero logo, startup-form image/video
// fields, user avatar). `accept` on <input type="file"> only filters the OS picker — users can
// still choose "All files" — so the picked file is re-checked here before it reaches the form.

// Matches one accept token: an extension (".png"), a MIME type ("image/png") or a wildcard ("image/*").
function matchesAcceptToken(file: File, token: string): boolean {
  const rule = token.trim().toLowerCase();
  if (!rule) return false;
  if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule);
  const type = file.type.toLowerCase();
  if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
  return type === rule;
}

/** True when the file matches the field's accept list (no list = anything goes). */
export function isFileAccepted(file: File, accept?: readonly string[]): boolean {
  if (!accept || accept.length === 0) return true;
  return accept.some((token) => matchesAcceptToken(file, token));
}

/** Human-readable accept list for messages: [".jpg", ".png"] → "JPG, PNG". */
export function formatAcceptList(accept: readonly string[]): string {
  const labels = accept.map((token) => token.trim().replace(/^\./, '').toUpperCase());
  return [...new Set(labels)].join(', ');
}

// Browser-loadable sources: remote URLs, object URLs, data URIs and same-origin paths.
const LOADABLE_URL = /^(https?:\/\/|blob:|data:|\/)/i;

/**
 * First URL of a stored upload value. The BE stores a JSON list of URLs; older rows may hold
 * one bare URL, or plain text (e.g. a startup name saved before startup_logo became an image
 * field) — that is not a URL and returns null so callers show their placeholder instead of a
 * broken image.
 */
export function getStoredAssetUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  let candidate: unknown = trimmed;
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      candidate = Array.isArray(parsed) ? parsed[0] : null;
    } catch {
      return null;
    }
  }
  return typeof candidate === 'string' && LOADABLE_URL.test(candidate) ? candidate : null;
}
